import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Flame, RefreshCw } from "lucide-react";
import HQLayout from "./HQLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TrendCard({ t, i }) {
  return (
    <article
      className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 md:p-7"
      data-testid={`hq-trend-${i}`}
    >
      <div className="flex items-baseline gap-3">
        <span className="font-display font-bold text-[#E53935] text-[12px] shrink-0">{String(i + 1).padStart(2, "0")}</span>
        <h3 className="font-display font-bold text-white text-[18px] md:text-[20px] tracking-[-0.015em] leading-[1.3]">{t.title}</h3>
      </div>
      <p className="mt-3 text-white/60 text-[13.5px] leading-[1.7]">{t.trend}</p>
      <div className="mt-4 rounded-xl bg-white/[0.04] border border-white/[0.06] px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/35 mb-1">The reference</div>
        <p className="text-white/70 text-[13px] leading-[1.65]">{t.reference}</p>
      </div>
      <div className="mt-3 border-l-2 border-[#E53935] pl-4">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#E53935] font-bold mb-1">For your slate</div>
        <p className="text-white/80 text-[13.5px] leading-[1.65]">{t.connection}</p>
      </div>
      {t.action && (
        <p className="mt-3.5 inline-flex items-start gap-2 text-[13px] text-white/55 leading-[1.6]">
          <ArrowUpRight className="w-3.5 h-3.5 mt-[2px] shrink-0 text-white/35" />
          {t.action}
        </p>
      )}
    </article>
  );
}

// Ida's Trend Room: her daily read of the ad world, wired to the active slate.
export default function HQTrends() {
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    setError(false);
    if (refresh) setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/hq/trends`, { params: refresh ? { refresh: true } : {} });
      setRoom(data);
    } catch (e) {
      setError(true);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <HQLayout>
      <main className="flex-1 px-6 md:px-10 py-8 md:py-10 max-w-[860px]" data-testid="hq-trends-page">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-white/40">
              <Flame className="w-3.5 h-3.5" /> Trend Room
            </div>
            <h1 className="mt-2 font-display font-extrabold text-white text-[26px] md:text-[32px] tracking-[-0.025em]">
              What the culture is doing today
            </h1>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[12.5px] text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50 shrink-0"
            data-testid="hq-trends-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Ida is scanning\u2026" : "Reopen the room"}
          </button>
        </div>

        {!room && !error && (
          <p className="mt-10 text-white/40 text-[14px] animate-pulse" data-testid="hq-trends-loading">
            Ida is connecting the culture to your slate{"\u2026"}
          </p>
        )}
        {error && (
          <p className="mt-10 text-white/40 text-[14px]" data-testid="hq-trends-error">
            The room would not open. Try again in a moment.
          </p>
        )}
        {room && (
          <>
            {room.date_line && (
              <p className="mt-6 font-serif-italic text-white/70 text-[16px] md:text-[17px] leading-[1.6]" data-testid="hq-trends-dateline">
                {room.date_line}
              </p>
            )}
            <div className="mt-8 space-y-5" data-testid="hq-trends-list">
              {(room.trends || []).map((t, i) => <TrendCard key={i} t={t} i={i} />)}
            </div>
            <p className="mt-10 text-[11px] text-white/25 leading-[1.6]">
              From Ida&rsquo;s knowledge of campaigns and culture, refreshed daily and read against your active projects.
            </p>
          </>
        )}
      </main>
    </HQLayout>
  );
}
